import { useState } from "react";
import { Link } from "@tanstack/react-router";
import type { StepView } from "@/app/queries";
import { Button } from "@/ui/Button";
import { Callout } from "@/ui/Callout";
import { CodeBlock } from "@/ui/CodeBlock";
import { plural } from "@/lib/plural";
import { Inline } from "./Inline";
import { blockOn, lines, quizItems, splitMistake, type QuizItem, type TickFn } from "./model";

/* Блоки шага по A0: «проверь себя», «типичные ошибки», «пример кода»,
   «заметки менторов». Рисуются только при blocks[key].on и непустом тексте. */

export function QuizBlock({ view, tick }: { view: StepView; tick: TickFn }) {
  if (!blockOn(view, "quiz")) return null;
  const qs = quizItems(view);
  return (
    <div id="quiz" className="scroll-mt-20">
      <Callout kind="quiz" tag="проверь себя" title={`${qs.length} ${plural(qs.length, "вопрос", "вопроса", "вопросов")}`}>
        <QuizBody view={view} tick={tick} />
      </Callout>
    </div>
  );
}

/** «Проверь себя» (S4/S8): вопросы с «показать ответ» и отметкой «знаю»
 *  (kind quiz, ключ — id вопроса); режим «блиц» — по одному вопросу. */
export function QuizBody({ view, tick }: { view: StepView; tick: TickFn }) {
  const qs = quizItems(view);
  const [blitz, setBlitz] = useState(false);
  const known = new Set(view.ticks.quiz);
  const tickable = qs.filter((q) => q.id);
  const knownCount = tickable.filter((q) => known.has(q.id)).length;
  const withAnswers = tickable.filter((q) => q.a.trim());
  if (blitz) return <Blitz items={withAnswers} known={known} tick={tick} onExit={() => setBlitz(false)} />;
  return (
    <div className="flex flex-col gap-3">
      {tickable.length > 0 && (
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="font-mono text-[11.5px] text-ink2" data-testid="quiz-known">
            знаю {knownCount} из {tickable.length}
          </span>
          <span className="flex-1" />
          {withAnswers.length > 0 && (
            <Button size="sm" onClick={() => setBlitz(true)}>
              блиц
            </Button>
          )}
        </div>
      )}
      <ol className="m-0 flex list-none flex-col gap-2 p-0">
        {qs.map((q, i) => (
          <QuizRow key={q.id || i} n={i + 1} item={q} known={known.has(q.id)} tick={tick} />
        ))}
      </ol>
    </div>
  );
}

function QuizRow({ n, item, known, tick }: { n: number; item: QuizItem; known: boolean; tick: TickFn }) {
  const [open, setOpen] = useState(false);
  const answerId = `quiz-answer-${item.id || n}`;
  const hasAnswer = Boolean(item.a.trim());
  return (
    <li id={item.id ? `quiz-${item.id}` : undefined} data-testid="quiz-item" className="rounded-sm border border-bd bg-bg2 px-3 py-2.5">
      <div className="flex items-start gap-2.5">
        <span className="font-mono text-[11.5px] leading-[1.6] text-mut">{n}</span>
        <span className="min-w-0 flex-1 text-pretty text-[13px] leading-[1.55] text-ink">{item.q}</span>
      </div>
      {(hasAnswer || item.id) && (
        <div className="mt-2 flex flex-wrap items-center gap-3 pl-[22px]">
          {hasAnswer && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls={answerId}
              className="min-h-[32px] cursor-pointer border-0 bg-transparent p-0 font-mono text-[11.5px] text-info hover:underline"
            >
              {open ? "скрыть ответ" : "показать ответ"}
            </button>
          )}
          <span className="flex-1" />
          {item.id && (
            <label className="flex min-h-[32px] cursor-pointer items-center gap-2 font-mono text-[11.5px] text-ink2">
              <input type="checkbox" checked={known} onChange={(e) => tick({ kind: "quiz", key: item.id, on: e.target.checked })} className="size-[15px] cursor-pointer accent-[var(--acc)]" />
              знаю
            </label>
          )}
        </div>
      )}
      {open && (
        <div id={answerId} className="mt-2 ml-[22px] text-pretty text-[13px] leading-[1.55] text-ink2" style={{ borderLeft: "2px solid var(--info)", paddingLeft: 10 }}>
          <Inline text={item.a} />
        </div>
      )}
    </li>
  );
}

/** Блиц: вопрос → «показать ответ» → «знаю» / «повторить»; итог — по ответам этого прохода. */
function Blitz({ items, known, tick, onExit }: { items: QuizItem[]; known: Set<string>; tick: TickFn; onExit: () => void }) {
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [results, setResults] = useState<Record<string, boolean>>({});
  const answer = (item: QuizItem, ok: boolean) => {
    if (ok !== known.has(item.id)) tick({ kind: "quiz", key: item.id, on: ok });
    setResults((r) => ({ ...r, [item.id]: ok }));
    setRevealed(false);
    setIdx((i) => i + 1);
  };
  const restart = () => {
    setIdx(0);
    setRevealed(false);
    setResults({});
  };
  if (idx >= items.length) {
    const ok = items.filter((q) => results[q.id]).length;
    const again = items.length - ok;
    return (
      <div className="flex flex-col gap-3" data-testid="blitz-result">
        <div className="font-mono text-[15px] font-semibold text-ink">
          знаю {ok} из {items.length}
        </div>
        <p className="m-0 text-[13px] text-ink2">
          {again > 0 ? `${again} ${plural(again, "вопрос", "вопроса", "вопросов")} — повторить: они остались без отметки «знаю».` : "Все вопросы отмечены «знаю»."}
        </p>
        <div className="flex flex-wrap gap-2.5">
          <Button size="sm" onClick={restart}>
            ещё раз
          </Button>
          <Button size="sm" variant="ghost" onClick={onExit}>
            к списку
          </Button>
        </div>
      </div>
    );
  }
  const item = items[idx]!;
  return (
    <div className="flex flex-col gap-3" data-testid="blitz">
      <div className="flex items-center gap-2.5">
        <span className="font-mono text-[11.5px] text-ink2">
          вопрос {idx + 1} из {items.length}
        </span>
        <span className="flex-1" />
        <Button size="sm" variant="ghost" onClick={onExit}>
          выйти из блица
        </Button>
      </div>
      <p className="m-0 text-pretty text-[14px] leading-[1.5] text-ink">{item.q}</p>
      {revealed ? (
        <>
          <div className="text-pretty text-[13px] leading-[1.55] text-ink2" style={{ borderLeft: "2px solid var(--info)", paddingLeft: 10 }}>
            <Inline text={item.a} />
          </div>
          <div className="flex flex-wrap gap-2.5">
            <Button size="sm" variant="primary" onClick={() => answer(item, true)}>
              знаю
            </Button>
            <Button size="sm" onClick={() => answer(item, false)}>
              повторить
            </Button>
          </div>
        </>
      ) : (
        <div>
          <Button size="sm" variant="primary" onClick={() => setRevealed(true)}>
            показать ответ
          </Button>
        </div>
      )}
    </div>
  );
}

export function MistakesBlock({ view }: { view: StepView }) {
  if (!blockOn(view, "mistakes")) return null;
  const ms = lines(view.content.blocks.mistakes?.text).map(splitMistake);
  return (
    <div id="mistakes" className="scroll-mt-20">
      <Callout kind="mistakes" tag="типичные ошибки" title={`${ms.length} ${plural(ms.length, "пункт", "пункта", "пунктов")}`}>
        <div className="flex flex-col gap-2.5">
          {ms.map((m, i) => (
            <div key={i} className="text-pretty">
              <span className="font-semibold text-ink">{m.t}</span>
              {m.d && <> — {m.d}</>}
            </div>
          ))}
        </div>
      </Callout>
    </div>
  );
}

export function CodeSection({ view }: { view: StepView }) {
  if (!blockOn(view, "code")) return null;
  const b = view.content.blocks.code!;
  return (
    <section id="code" className="scroll-mt-20">
      <div className="mb-2.5 flex items-baseline gap-2.5">
        <h2 className="m-0 font-mono text-[15px] font-semibold">Пример кода</h2>
        <span className="font-mono text-[11.5px] text-mut">python</span>
      </div>
      <CodeBlock code={b.text} label={b.label || "python"} />
    </section>
  );
}

/** Строка заметки из базы знаний: «- [Название](/notes/slug) — описание». Остальное — текст как есть. */
export function noteLink(line: string): { label: string; href: string; rest: string } | null {
  const m = /^(?:[-*]\s+)?\[([^\]]+)\]\((\/notes\/[^)\s]+)\)\s*(?:[—–-]\s*(.*))?$/.exec(line.trim());
  return m ? { label: m[1]!, href: m[2]!, rest: (m[3] ?? "").trim() } : null;
}

export function NotesBlock({ view }: { view: StepView }) {
  if (!blockOn(view, "notes")) return null;
  const ns = lines(view.content.blocks.notes?.text);
  return (
    <div id="notes" className="scroll-mt-20">
      <Callout kind="notes" tag="заметки менторов" title={`${ns.length} ${plural(ns.length, "конспект", "конспекта", "конспектов")}`}>
        <div className="flex flex-col gap-2.5">
          {ns.map((n, i) => {
            const link = noteLink(n);
            return (
              <div key={i} className="flex gap-2 font-mono text-[12.5px]">
                <span aria-hidden className="text-acc">
                  ↗
                </span>
                {link ? (
                  <span>
                    <Link to={link.href} className="text-ink underline decoration-bd2 underline-offset-2 hover:decoration-acc">
                      {link.label}
                    </Link>
                    {link.rest && <span className="text-ink2"> — {link.rest}</span>}
                  </span>
                ) : (
                  <span>{n}</span>
                )}
              </div>
            );
          })}
        </div>
      </Callout>
    </div>
  );
}
